import React from "react";
import { Icon, notify } from "./registry";
import { cents } from "./lib/printkit";
import {
  garmentOf, garmentSizes, lineQty, mockupSvg, mockupViews, apparelEstimate, apparelMin, validateApparelLine,
} from "./lib/printorders";
import type { ApparelCatalog, ApparelLine, Garment, PriceRow } from "./lib/printorders";
/* ============================================================
   Fence Works — Print Kit · Apparel
   Logo apparel for the crew: pick a garment, a color, where the logo
   goes and how many of each size; the mockup draws the contractor's
   logo on a clean garment in that color. Lines land in the same order
   as the print pieces (settings.printKit.apparel) and the service
   validates and prices them again (server/src/printorders.js).
   ============================================================ */

/** the garment drawn in a color with the logo in place — one view */
export function GarmentMockup({ garment, colorHex, placementId, logo, view = "front", style }: { garment: Garment; colorHex?: string; placementId?: string; logo?: string | null; view?: string; style?: any }) {
  const uid = React.useId();
  const html = React.useMemo(() => mockupSvg(garment, { colorHex, placementId, logo, view, idPrefix: "g" + uid }), [garment, colorHex, placementId, logo, view, uid]);
  const vb = String((garment.views[view] && garment.views[view].viewBox) || "0 0 400 440").split(/\s+/).map(Number);
  return <div className="pk-mockup" style={{ aspectRatio: vb[2] + " / " + vb[3], ...(style || {}) }} dangerouslySetInnerHTML={{ __html: html }} />;
}

const newLineId = () => "ap" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
function freshLine(cat: ApparelCatalog, garmentId?: string, keep?: Partial<ApparelLine>): ApparelLine {
  const g = garmentOf(cat, garmentId || "") || cat.garments[0];
  const colorId = keep && keep.colorId && g.colors.some((c) => c.id === keep.colorId) ? keep.colorId : g.colors[0].id;
  const placementId = keep && keep.placementId && g.placements.some((p) => p.id === keep.placementId) ? keep.placementId : g.placements[0].id;
  const allowed = garmentSizes(cat, g);
  const sizes: Record<string, number> = {};
  for (const k in (keep && keep.sizes) || {}) if (allowed.includes(k) && Number((keep as any).sizes[k]) > 0) sizes[k] = Number((keep as any).sizes[k]);
  return { id: (keep && keep.id) || newLineId(), garmentId: g.id, colorId, placementId, sizes, logo: (keep && keep.logo) || null };
}

export function ApparelBuilder({ catalog, kit, saveKit, brandLogo, prices, editId, setEditId, onAdded, Picker, jumpToBrand }: {
  catalog: ApparelCatalog; kit: any; saveKit: (p: any) => void; brandLogo: string; prices: PriceRow[] | null;
  editId: string | null; setEditId: (id: string | null) => void; onAdded: () => void; Picker: any; jumpToBrand: () => void;
}) {
  const lines: ApparelLine[] = kit.apparel || [];
  const editing = editId ? lines.find((l) => l.id === editId) || null : null;
  const [draft, setDraft] = React.useState<ApparelLine>(() => (editing ? { ...editing } : freshLine(catalog, "tee")));
  React.useEffect(() => { if (editing) setDraft({ ...editing }); }, [editId]);
  const g = garmentOf(catalog, draft.garmentId) || catalog.garments[0];
  const color = g.colors.find((c) => c.id === draft.colorId) || g.colors[0];
  const placement = g.placements.find((p) => p.id === draft.placementId) || g.placements[0];
  const logo = draft.logo || brandLogo || null;
  const priceRows = prices || [];
  const errors = validateApparelLine(draft, catalog, priceRows);
  const qty = lineQty(draft.sizes);
  const min = apparelMin(g, priceRows);
  const est = apparelEstimate(draft, g, catalog, priceRows);
  const views = mockupViews(g, placement.id);
  const setSize = (k: string, v: string) => setDraft((d) => { const n = Math.max(0, Math.floor(Number(v.replace(/[^0-9]/g, "")) || 0)); const s = { ...d.sizes }; if (n) s[k] = n; else delete s[k]; return { ...d, sizes: s }; });
  const youth = g.youth ? catalog.youthSizes || [] : [];
  const fromPrice = (gg: Garment) => { const r = priceRows.filter((p) => p.templateId === "apparel:" + gg.id).sort((a, b) => a.priceCents - b.priceCents)[0]; return r ? r.priceCents : null; };

  function commit() {
    if (errors.length) { notify(errors[0]); return; }
    if (!logo) { notify("Add your logo in the Brand panel first — it's what gets printed."); jumpToBrand(); return; }
    const line = { ...draft, logo: draft.logo || null };
    const next = editing ? lines.map((l) => (l.id === editing.id ? line : l)) : [...lines, line];
    saveKit({ apparel: next });
    notify(editing ? g.name + " line updated." : g.name + " added — " + qty + " pcs, " + color.name.toLowerCase() + ", " + placement.label.toLowerCase() + ".");
    setEditId(null);
    setDraft(freshLine(catalog, g.id, { colorId: draft.colorId, placementId: draft.placementId }));
    if (!editing) onAdded();
  }

  return (
    <div className="pk-ap-grid" style={{ display: "grid", gridTemplateColumns: "220px minmax(300px, 1fr) 380px", gap: 14, alignItems: "start" }}>
      {/* ---- garments ---- */}
      <div className="card pk-ap-list" style={{ padding: 8 }}>
        {catalog.garments.map((gg) => {
          const on = gg.id === g.id; const c = gg.colors.find((x) => x.id === draft.colorId) || gg.colors[0]; const from = fromPrice(gg);
          return (
            <button key={gg.id} className={"sb-subitem" + (on ? " active" : "")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 8, textAlign: "left" }}
              onClick={() => setDraft((d) => freshLine(catalog, gg.id, { ...d, id: d.id }))}>
              <GarmentMockup garment={gg} colorHex={c.hex} placementId={gg.placements[0].id} logo={logo} style={{ width: 40, height: 40, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: on ? 800 : 600 }}>{gg.name}</div>
                <div className="faint" style={{ fontSize: 10.5 }}>{from != null ? "from " + cents(from) + " each" : "min " + apparelMin(gg, priceRows)} · {gg.decoration && gg.decoration.toLowerCase()}</div>
              </span>
            </button>
          );
        })}
      </div>

      {/* ---- the mockup ---- */}
      <div className="card pad" style={{ padding: 12, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>{g.name} <span className="faint" style={{ fontWeight: 500, fontSize: 12 }}>· {color.name} · {placement.label}</span></div>
        <div className="muted" style={{ fontSize: 12 }}>{g.blurb} {g.decoration ? g.decoration + "." : ""}</div>
        <div className="pk-ap-stage" style={{ display: "grid", gridTemplateColumns: views.length > 1 ? "1fr 1fr" : "1fr", gap: 12, marginTop: 10, background: "#e7e3da", borderRadius: 10, padding: 16 }}>
          {views.map((v) => (
            <div key={v} style={{ textAlign: "center" }}>
              <GarmentMockup garment={g} colorHex={color.hex} placementId={placement.id} logo={logo} view={v} style={{ maxWidth: views.length > 1 ? 300 : 380, margin: "0 auto" }} />
              <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{v === "front" ? "Front" : "Back"}{placement.locations.filter((l) => l.view === v).map((l) => l.area ? " · print area " + l.area : "").join("")}</div>
            </div>
          ))}
        </div>
        <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>A mockup — screen colors are close, not exact. The printer gets your logo file and this placement; {g.decoration && /embroider/i.test(g.decoration) ? "embroidery simplifies fine detail and gradients." : "a logo on a transparent background prints cleanest."}</p>
      </div>

      {/* ---- options ---- */}
      <div className="card pad" style={{ padding: 14 }}>
        <div className="kicker" style={{ marginBottom: 8 }}>Color <span className="faint" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>— {color.name}</span></div>
        <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 14 }}>
          {g.colors.map((c) => (
            <button key={c.id} title={c.name} aria-label={c.name} aria-pressed={c.id === color.id} onClick={() => setDraft((d) => ({ ...d, colorId: c.id }))}
              style={{ width: 30, height: 30, borderRadius: 99, background: c.hex, border: "1.5px solid var(--line)", boxShadow: c.id === color.id ? "0 0 0 2px var(--bg, #fff), 0 0 0 4px var(--teal)" : "inset 0 0 0 1px rgba(0,0,0,.18)", cursor: "pointer" }} />
          ))}
        </div>

        <div className="kicker" style={{ marginBottom: 8 }}>Logo placement</div>
        <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
          {g.placements.map((p) => { const on = p.id === placement.id; return (
            <button key={p.id} className={"toggle-row" + (on ? " on" : "")} onClick={() => setDraft((d) => ({ ...d, placementId: p.id }))}>
              <span className={"cbox" + (on ? " on" : "")}>{on ? <Icon name="check" size={11} /> : null}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</span>
              <span className="faint" style={{ fontSize: 11, marginLeft: "auto" }}>{p.locations.map((l) => l.area).filter(Boolean).join(" + ")}</span>
            </button>); })}
        </div>

        <div className="row between" style={{ marginBottom: 8 }}>
          <div className="kicker">Sizes</div>
          <span className={"chip " + (qty && qty < min ? "amber" : qty ? "teal" : "")} style={{ fontSize: 11 }}>{qty} pcs · min {min}</span>
        </div>
        <div className="pk-sizes" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))", gap: 6, marginBottom: youth.length ? 8 : 14 }}>
          {g.sizes.map((k) => <SizeBox key={k} k={k} label={(catalog.sizeLabels || {})[k] || k} v={draft.sizes[k]} up={(catalog.upchargeSizes || []).includes(k)} onChange={setSize} />)}
        </div>
        {youth.length > 0 && (
          <details style={{ marginBottom: 14 }} open={youth.some((k) => draft.sizes[k])}>
            <summary className="faint" style={{ fontSize: 12, cursor: "pointer" }}>Youth sizes</summary>
            <div className="pk-sizes" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))", gap: 6, marginTop: 6 }}>
              {youth.map((k) => <SizeBox key={k} k={k} label={(catalog.sizeLabels || {})[k] || k} v={draft.sizes[k]} onChange={setSize} />)}
            </div>
          </details>
        )}

        <div className="kicker" style={{ marginBottom: 8 }}>Logo</div>
        <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
          <div style={{ width: 84, height: 44, borderRadius: 8, background: isDark(color.hex) ? color.hex : "#f3f1ec", border: "1px solid var(--line)", display: "grid", placeItems: "center", overflow: "hidden" }}>
            {logo ? <img src={logo} alt="Logo" style={{ maxWidth: "88%", maxHeight: "80%", objectFit: "contain" }} /> : <span className="faint" style={{ fontSize: 10 }}>no logo</span>}
          </div>
          <Picker label={draft.logo ? "Replace" : "Different logo for this line"} maxPx={1600} small onPick={(u: string) => setDraft((d) => ({ ...d, logo: u }))} />
          {draft.logo && <button className="btn btn-ghost btn-sm" onClick={() => setDraft((d) => ({ ...d, logo: null }))}>Use brand logo</button>}
        </div>
        <p className="faint" style={{ fontSize: 11, margin: "0 0 14px" }}>{!logo ? <span style={{ color: "var(--amber)" }}>No logo yet — add it in the Brand panel above.</span> : draft.logo ? "This line uses its own logo." : "Your brand logo. On dark garments a light version reads better — upload it here."}</p>

        <div style={{ padding: "10px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, marginBottom: 10 }}>
          {est.unitCents != null ? (
            <>
              <div className="row between"><span style={{ fontSize: 13 }}>{cents(est.unitCents)} each × {qty}</span><b style={{ fontSize: 16 }}>{cents(est.totalCents)}</b></div>
              {est.upchargeCents > 0 && <div className="faint" style={{ fontSize: 11 }}>includes {cents(est.upchargeCents)} for 2XL / 3XL</div>}
              {est.next && <div className="faint" style={{ fontSize: 11 }}>{est.next.quantity - qty} more and it's {cents(est.next.unitCents)} each.</div>}
            </>
          ) : prices === null ? <div className="faint" style={{ fontSize: 12 }}>Loading prices…</div>
            : est.from != null ? <div style={{ fontSize: 12.5 }}>{cents(est.from)} each from {est.fromQty} pieces.</div>
              : <div style={{ fontSize: 12.5 }}>Priced by the print desk after you order — you'll get a pay link by email.</div>}
        </div>
        {errors.length > 0 && qty > 0 && <div style={{ fontSize: 12, color: "var(--amber)", marginBottom: 8 }}>{errors[0]}</div>}
        <div className="row gap-8">
          <button className="btn btn-primary" disabled={errors.length > 0} onClick={commit}><Icon name={editing ? "check" : "plus"} size={14} />{editing ? "Update line" : "Add to order"}</button>
          {editing && <button className="btn btn-ghost" onClick={() => { setEditId(null); setDraft(freshLine(catalog, g.id)); }}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}

const isDark = (hex: string) => { const n = parseInt(String(hex || "#888888").slice(1), 16); return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 < 0.45; };

function SizeBox({ k, label, v, up, onChange }: any) {
  return (
    <label style={{ display: "grid", gap: 3, fontSize: 11.5, fontWeight: 700, textAlign: "center" }}>
      <span>{label}{up ? <span className="faint" style={{ fontWeight: 500 }}> +</span> : null}</span>
      <input className="input tnum" inputMode="numeric" aria-label={label + " quantity"} value={v ? String(v) : ""} placeholder="0" onChange={(e) => onChange(k, e.target.value)} style={{ textAlign: "center", padding: "6px 4px" }} />
    </label>
  );
}

/** an apparel line as it reads in the cart and in the orders list */
export function ApparelLineSummary({ catalog, line, logo, right }: { catalog: ApparelCatalog | null; line: any; logo?: string | null; right?: any }) {
  const g = garmentOf(catalog, line.garmentId);
  const c = g && g.colors.find((x) => x.id === line.colorId);
  const p = g && g.placements.find((x) => x.id === line.placementId);
  const order = (catalog && catalog.sizes) || [];
  const sizes = Object.keys(line.sizes || {}).filter((k) => Number(line.sizes[k]) > 0).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return (
    <div className="row gap-10" style={{ alignItems: "center" }}>
      {g ? <GarmentMockup garment={g} colorHex={(c && c.hex) || line.colorHex} placementId={line.placementId} logo={logo} style={{ width: 48, height: 48, flexShrink: 0 }} /> : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>{(g && g.name) || line.name} <span className="faint" style={{ fontWeight: 500 }}>× {lineQty(line.sizes)}</span></div>
        <div className="faint" style={{ fontSize: 11 }}>{(c && c.name) || line.colorName} · {(p && p.label) || line.placementLabel}</div>
        <div className="mono" style={{ fontSize: 10.5, color: "var(--fg-3)" }}>{sizes.map((k) => k + " " + line.sizes[k]).join(" · ")}</div>
      </div>
      {right}
    </div>
  );
}
